import { motion } from "framer-motion";
import { Play } from "lucide-react";
import { Chuckle, Drip, Shiver, Bob } from "./Characters";
import { Reveal } from "./Reveal";
import { FILMS } from "../data/films";

const TAGS = {
  Laughter: "bg-sun text-ink",
  Tears: "bg-sky text-ink",
  Fears: "bg-grape text-white",
};

export const FilmCard = ({ film, testId }) => (
  <motion.article whileHover={{ y: -6 }} transition={{ type: "spring", stiffness: 300, damping: 20 }}
    data-testid={testId} className="group overflow-hidden rounded-[28px] bg-paper text-ink">
    <div className="relative aspect-video overflow-hidden bg-ink/10">
      {film.thumbnail ? (
        <img src={film.thumbnail} alt={`Still from ${film.title}`} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
      ) : (
        <div className="grid h-full w-full place-items-center font-body text-sm font-semibold text-ink/70">[thumbnail]</div>
      )}
      <span className={`absolute left-3 top-3 rounded-full px-3 py-1 font-body text-sm font-semibold ${TAGS[film.category] || "bg-paper text-ink"}`}>{film.category}</span>
    </div>
    <div className="flex items-end justify-between gap-4 p-5">
      <div className="min-w-0">
        <h3 className="truncate font-head text-2xl">{film.title}</h3>
        <p className="truncate font-body text-base text-ink/80">{film.creator}</p>
      </div>
      <a href={film.url || "#showcase"} data-testid={`${testId}-watch`} className="btn-pink font-head inline-flex shrink-0 items-center gap-1.5 rounded-full bg-pop px-4 py-2 text-base text-ink">
        <Play size={16} fill="currentColor" /> Watch
      </a>
    </div>
  </motion.article>
);

const EmptyState = () => (
  <div data-testid="showcase-empty-state" className="flex flex-col items-center rounded-[40px] border-2 border-dashed border-white/25 px-6 py-16 text-center sm:py-24">
    <div className="flex items-end gap-2 sm:gap-6">
      <Bob><Chuckle className="h-20 w-20 sm:h-32 sm:w-32" /></Bob>
      <Bob delay={0.6}><Drip className="h-24 w-24 sm:h-40 sm:w-40" /></Bob>
      <Bob delay={1.2}><Shiver className="h-20 w-20 sm:h-32 sm:w-32" /></Bob>
    </div>
    <p className="mt-8 font-head text-3xl sm:text-5xl">First films land here soon.</p>
  </div>
);

const TemplatePreview = () => (
  <div className="mt-14 grid items-center gap-8 md:grid-cols-[1fr_1.1fr]">
    <div>
      <p className="font-body text-base font-semibold text-paper/80">Card preview</p>
      <p className="mt-2 max-w-md font-body text-lg leading-relaxed text-paper/90">This is how each finalist will appear. No card ever shows whether a film used AI.</p>
    </div>
    <div className="max-w-md md:justify-self-end">
      <FilmCard testId="film-card-template" film={{ title: "[Film title]", creator: "[Creator name]", category: "Laughter" }} />
    </div>
  </div>
);

export const Showcase = () => {
  const films = Array.isArray(FILMS) ? FILMS : [];
  return (
    <section id="showcase" data-testid="showcase-section" className="bg-ink py-20 text-paper sm:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-10">
        <Reveal className="mb-12 flex flex-wrap items-end justify-between gap-4">
          <h2 className="font-head text-5xl leading-none tracking-tight sm:text-6xl lg:text-7xl">The shortlist</h2>
          <p className="font-body text-lg text-paper/85">Finalists in Laughter, Tears and Fears.</p>
        </Reveal>
        {films.length === 0 ? (
          <Reveal><EmptyState /><TemplatePreview /></Reveal>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {films.map((f, i) => <FilmCard key={f.id ?? i} film={f} testId={`film-card-${i}`} />)}
          </div>
        )}
      </div>
    </section>
  );
};
